import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { env } from '@/config/env';
import { useBranch } from '@/features/branch/branch-context';
import { apiHooks, BRANCH_HEADER, CLIENT_HEADER } from '@/lib/api/client';
import { CHATS_KEY } from './api';

/** One parsed SSE frame. */
export interface ChatStreamEvent {
  event: string;
  data: unknown;
}

/** Splits a text/event-stream buffer into frames; returns the unfinished rest. */
export function parseFrames(buffer: string): {
  frames: ChatStreamEvent[];
  rest: string;
} {
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';
  const frames: ChatStreamEvent[] = [];
  for (const part of parts) {
    let event = 'message';
    let data = '';
    for (const line of part.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data += line.slice(5).trim();
    }
    if (!data) continue;
    try {
      frames.push({ event, data: JSON.parse(data) as unknown });
    } catch {
      // A broken frame is skipped; the next refetch catches up.
    }
  }
  return { frames, rest };
}

const MIN_RETRY_MS = 3_000;
const MAX_RETRY_MS = 60_000;

/**
 * RL-1674 (RL-1150 stream): live updates for the nurses' inbox. Reads
 * GET /clinic-chats/events with fetch() (the browser's EventSource cannot
 * send the bearer token) and refetches the list, the open chat and the
 * sidebar count on each event (ids and counters only: the content always
 * comes from the GET routes). Reconnects with a growing delay; refreshes the
 * token first when the stream says it expired. The 30 s polling of the
 * queries stays as the fallback.
 */
export function useClinicChatLive(enabled = true) {
  const qc = useQueryClient();
  const { selected, canSelectAll } = useBranch();
  const branch = selected?.id ?? (canSelectAll ? null : undefined);

  useEffect(() => {
    if (!enabled || branch === undefined) return;
    const stop = new AbortController();
    let delay = MIN_RETRY_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const refresh = (event: string) => {
      if (event === 'chat.unread') {
        void qc.invalidateQueries({ queryKey: ['shell', 'counts'] });
        return;
      }
      if (
        event === 'chat.message' ||
        event === 'chat.thread' ||
        event === 'chat.resync'
      ) {
        void qc.invalidateQueries({ queryKey: CHATS_KEY });
        void qc.invalidateQueries({ queryKey: ['shell', 'counts'] });
      }
    };

    const connect = async () => {
      let end: string | null = null;
      try {
        const token = apiHooks.getAccessToken?.();
        const res = await fetch(`${env.apiBaseUrl}/clinic-chats/events`, {
          headers: {
            Accept: 'text/event-stream',
            [CLIENT_HEADER]: 'admin',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(branch ? { [BRANCH_HEADER]: branch } : {}),
          },
          signal: stop.signal,
        });
        if (res.status === 401) end = 'token_expired';
        else if (!res.ok || !res.body) end = 'error';
        else {
          delay = MIN_RETRY_MS;
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parsed = parseFrames(buffer);
            buffer = parsed.rest;
            for (const f of parsed.frames) {
              if (f.event === 'chat.end')
                end = (f.data as { reason?: string }).reason ?? 'end';
              else refresh(f.event);
            }
          }
        }
      } catch {
        if (stop.signal.aborted) return;
        end = 'error';
      }
      if (stop.signal.aborted || end === 'signed_out') return;
      if (end === 'token_expired')
        await apiHooks.refreshSession?.().catch(() => false);
      // Catch up on anything missed while disconnected.
      refresh('chat.resync');
      timer = setTimeout(() => void connect(), delay);
      delay = Math.min(delay * 2, MAX_RETRY_MS);
    };

    void connect();
    return () => {
      stop.abort();
      clearTimeout(timer);
    };
  }, [enabled, branch, qc]);
}
